import { ComponentType, lazy, LazyExoticComponent } from 'react';

import {
  getRetryableChunkUrl,
  handleChunkExhausted,
  importChunkAtRetryUrl,
  isChunkLoadMessage,
  isStylesheetPreloadFailure,
} from './chunkErrorHandler';

export interface LoadableComponent<T extends ComponentType<any>>
  extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
  load: () => Promise<{ default: T }>;
}

/**
 * Typed error class for chunk loading failures. The `isChunkLoadError` flag
 * allows error boundaries to distinguish chunk errors from real application
 * errors without relying on string matching against the error name.
 */
export class ChunkLoadError extends Error {
  readonly isChunkLoadError = true;

  constructor(message: string) {
    super(message);
    this.name = 'ChunkLoadError';
  }
}

/**
 * Type-narrowing predicate for ChunkLoadError instances.
 */
export function isChunkLoadError(error: unknown): error is ChunkLoadError {
  return (
    error instanceof Error &&
    (error as ChunkLoadError).isChunkLoadError === true
  );
}

// React components are functions, or objects for memo/forwardRef/lazy.
function isComponentExport(value: unknown): boolean {
  return (
    typeof value === 'function' || (typeof value === 'object' && value !== null)
  );
}

/**
 * Wraps a dynamic import loader with validation and retry logic.
 *
 * In some browser/CDN failure modes, `import()` resolves with `undefined`
 * instead of rejecting. React's `lazy()` then stores `_status = 1, _result =
 * undefined` and crashes with "can't access property 'default', p._result is
 * undefined" when rendering. This wrapper validates the resolved module and
 * throws a typed ChunkLoadError instead, ensuring the error boundary handles
 * it rather than React entering a broken state.
 *
 * Retries up to `retries` times with exponential backoff before giving up.
 * When a failure names the chunk's script URL, each retry imports the chunk at
 * a new cache-busting URL (`?retry=n`): Chrome fails a retry of the same URL
 * without making a request, and the browser may be holding a bad cached copy.
 * When it gives up on a chunk failure it hands the failure to
 * `handleChunkExhausted` once, then rethrows the original error unchanged so
 * the error boundary still sees it. Other errors (a real bug thrown while the
 * module evaluates) are rethrown without triggering chunk recovery.
 */
export async function retryLoader<T>(
  loader: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 200,
): Promise<{ default: T }> {
  // The chunk failure that started the retries, and the URL it names.
  let originalError: unknown;
  let retryChunkUrl: string | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const module = retryChunkUrl
        ? await importChunkAtRetryUrl(retryChunkUrl, attempt)
        : await loader();

      if (!module || typeof module.default === 'undefined') {
        throw new ChunkLoadError(
          `Chunk resolved without a default export (attempt ${attempt + 1})`,
        );
      }
      // The retry imports the URL the error named, which is not guaranteed to
      // be this loader's module; only a component is a successful load.
      if (retryChunkUrl && !isComponentExport(module.default)) {
        throw new ChunkLoadError(
          `Retried chunk did not export a component (attempt ${attempt + 1})`,
        );
      }

      return module;
    } catch (caught) {
      if (attempt === 0) {
        originalError = caught;
        retryChunkUrl = getRetryableChunkUrl(caught);
      }
      // Report the failure that started the retries, not a retry of it. A
      // retry that loaded the chunk but threw while running it is a real bug,
      // so that error is the one to surface.
      const error =
        retryChunkUrl && isChunkLoadMessage(caught) ? originalError : caught;

      // A stylesheet preload failure is never retried (see
      // isStylesheetPreloadFailure): hand it straight to recovery.
      const isLastAttempt =
        attempt === retries || isStylesheetPreloadFailure(error);
      if (isLastAttempt) {
        if (isChunkLoadMessage(error)) {
          handleChunkExhausted(error, { attempts: attempt + 1 });
        }
        throw error;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, delayMs * 2 ** attempt),
      );
    }
  }

  // TypeScript requires this; the loop above always throws or returns.
  throw new ChunkLoadError('Failed to load chunk after retries');
}

/**
 * Drop-in replacement for @loadable/component that works with Vite.
 * This wrapper makes React.lazy compatible with existing loadable() calls,
 * and adds retry + validation logic to prevent broken React lazy state when
 * chunks fail to load from the CDN.
 *
 * @param loader - Dynamic import function that returns a Promise of the component module
 * @returns A lazy-loaded component with preload methods attached
 */
export default function loadable<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
): LoadableComponent<T> {
  const resilientLoader = () => retryLoader(loader);

  const LazyComponent = lazy(resilientLoader) as LoadableComponent<T>;

  // Add preload() and load() methods for compatibility with any existing calls
  // These allow you to imperatively trigger the loading of a component
  LazyComponent.preload = resilientLoader;
  LazyComponent.load = resilientLoader;

  return LazyComponent;
}
